import { PushPin } from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ReactComponent as SetPinIcon } from 'assets/set-pin-icon.svg';
import CountryFlag from 'components/base/CountryFlag';
import LocalStorageKey from 'config/localStorageKey';
import { useCustomerPreferences } from 'features/customerPreferences/useCustomerPreferences';
import { useUpdateCustomerPreferences } from 'features/customerPreferences/useUpdateCustomerPreferences';
import {
  getEmbeddingModelsByLanguageAndAudience,
  getGetEmbeddingModelLanguageAndCountryById,
} from 'features/embeddingModels/store/selectors';
import { EmbeddingModel } from 'features/embeddingModels/store/types';
import { sortModelsByLanguageAlphabetically } from 'features/embeddingModels/store/utils/sortModelsByLanguage';
import { MouseEvent, ReactElement, useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { useMenu } from 'utils/hooks/useMenu';

type Props = {
  value: string;
  onSelect: (embeddingModelId: string) => void;
  customTrigger: (props: {
    onClick: (event: MouseEvent<HTMLElement>) => void;
    embeddingModel: EmbeddingModel | undefined;
    open: boolean;
  }) => ReactElement;
};

export const LanguageSelect = ({ value, onSelect, customTrigger }: Props) => {
  const getModelLanguageAndCountryById = useAppSelector(getGetEmbeddingModelLanguageAndCountryById);
  const models = useAppSelector(getEmbeddingModelsByLanguageAndAudience);
  const sortedModels = useMemo(() => sortModelsByLanguageAlphabetically(models), [models]);

  const preferencesQuery = useCustomerPreferences();
  const { data: preferences } = preferencesQuery;
  const { mutateAsync: updatePreferences } = useUpdateCustomerPreferences();
  const defaultLanguageModelId = models.find(
    model =>
      model.language === preferences?.preferredLanguage &&
      model.country === preferences?.preferredLanguageCountry
  )?.id;

  const handleSetPreferredLanguage = (value: EmbeddingModel) => {
    const { language, country } = getModelLanguageAndCountryById(value.id);
    updatePreferences({ preferredLanguage: language, preferredLanguageCountry: country });
    localStorage.setItem(LocalStorageKey.AiWriterModel, String(value.id));
  };

  const { anchorEl, isOpen, onTriggerClick, onMenuClose } = useMenu();

  const handleItemClick = (modelId: string) => {
    onSelect(modelId);
    onMenuClose();
  };

  return (
    <>
      {customTrigger({
        onClick: onTriggerClick,
        embeddingModel: models.find(model => model.id === value),
        open: isOpen,
      })}
      <StyledMenu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        {sortedModels.map(model => (
          <StyledMenuItem
            key={`${model.country}-${model.language}`}
            onClick={() => handleItemClick(model.id)}
            selected={value === model.id}
          >
            <ListItemIcon>
              <CountryFlag country={model.country} language={model.language} />
            </ListItemIcon>

            <ListItemText>{model.label}</ListItemText>
            {defaultLanguageModelId && defaultLanguageModelId === model.id ? (
              <StyledPinIconActive fontSize="small" />
            ) : (
              model.id !== defaultLanguageModelId && (
                <StyledPinIcon
                  fontSize="small"
                  className="pinIcon"
                  onClick={event => {
                    event.stopPropagation();
                    handleSetPreferredLanguage(model);
                  }}
                />
              )
            )}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
};

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    minWidth: '250px',
  },
});

const StyledPinIconActive = styled(PushPin)`
  &,
  & > svg {
    color: ${({ theme }) => theme.colors.componentsIconActive};
  }
`;

const StyledPinIcon = styled(SetPinIcon)`
  &,
  & > svg {
    color: ${({ theme }) => theme.colors.componentsIconDefault};
  }

  display: none;
`;

const PinIconWrapper = styled.div`
  display: contents;
  cursor: pointer;
`;

const StyledMenuItem = styled(MenuItem)`
  &:hover {
    ${StyledPinIcon} {
      display: block;
    }
  }
`;
